import { useEffect, useMemo, useState } from "react";

import { SportPlay } from "../../../types";

export interface PlayWithSelected {
  selected: boolean;
}

export interface UsePlaysWithSelectionProps<TPlay> {
  activePlayId: string | undefined;
  plays: TPlay[] | undefined;
  playbooksPage?: boolean;
  selectedPlaybookPlayIds?: string[];
}

export const usePlaysWithSelection = <TPlay extends SportPlay>({
  activePlayId,
  plays,
  playbooksPage = false,
  selectedPlaybookPlayIds = [],
}: UsePlaysWithSelectionProps<TPlay>) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([...(activePlayId ? [activePlayId] : [])]);

  useEffect(() => {
    // Changed selected IDs if 1 or 0 items are selected
    if (activePlayId && selectedIds.length <= 1) {
      setSelectedIds([activePlayId]);
    }
  }, [activePlayId]);

  const handleCheckboxChange = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const dataWithSelected = useMemo(() => {
    return plays?.map((play) => ({ ...play, selected: selectedIds.includes(play.id || "") })) || [];
  }, [plays, selectedIds]);

  const allSelected = dataWithSelected.every((play) => play.selected);
  const someSelected = dataWithSelected.some((play) => play.selected);
  const allSelectedButPlaybookPlays = dataWithSelected
    .filter((play) => !selectedPlaybookPlayIds?.includes(play.id || ""))
    .every((play) => play.selected);

  const handleCheckAll = () => {
    if (allSelected || (playbooksPage && allSelectedButPlaybookPlays)) {
      setSelectedIds([]);
    } else {
      const ids = playbooksPage
        ? dataWithSelected
            .filter((play) => !selectedPlaybookPlayIds?.includes(play.id || ""))
            .map((play) => play.id || "")
        : dataWithSelected.map((play) => play.id || "");
      setSelectedIds(ids);
    }
  };

  return {
    dataWithSelected,
    handleCheckboxChange,
    selectedIds,
    setSelectedIds,
    allSelected,
    someSelected,
    handleCheckAll,
  };
};

export type PlaysWithSelection = ReturnType<typeof usePlaysWithSelection>;
